import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Alert,
  Button,
} from "reactstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend,
} from "recharts";
import { ClipLoader } from "react-spinners";
import {
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Building2,
  Wallet,
  Gift,
  AlertCircle,
  BarChart3,
} from "lucide-react";
import { 
  hasPagePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getAdminDashboard } from "../../components/Api/GmsAdminDashboard";

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // companyId from login response is actually the businessId
  const companyId = authUser.companyId || user.user_Company_Id || null;
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: companyId,
    businessId: companyId, // companyId is the businessId in the system
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

// Tile accent colors
const TILE_ACCENT_STYLES = {
  primary: { bg: '#556ee6', light: '#e7e9f6' },
  info: { bg: '#50a5f1', light: '#e6f2fc' },
  warning: { bg: '#f1b44c', light: '#fef5e7' },
  danger: { bg: '#f46a6a', light: '#fef0f0' },
  success: { bg: '#34c38f', light: '#e6f7f0' },
  purple: { bg: '#8b5cf6', light: '#f3f0ff' },
  secondary: { bg: '#74788d', light: '#f0f0f2' },
};

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const abortRef = useRef(null);
  const inFlightRef = useRef(false);

  const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAdminDashboard');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  const loadDashboardData = useCallback(async ({ background = false } = {}) => {
    try {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      // Abort any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (background) setIsRefreshing(true);
      else setInitialLoading(true);
      setError(null);
      
      // Convert businessId and branchId to numbers if they exist, otherwise pass null
      // Both are optional - the API should handle null values gracefully
      const businessId = session.businessId ? Number(session.businessId) : null;
      const branchId = session.branchId ? Number(session.branchId) : null;
      
      const filter = {
        businessId: businessId,
        branchId: branchId,
        today: new Date().toISOString().split('T')[0]
      };

      const response = await getAdminDashboard(filter, { signal: controller.signal });
      
      if (response && response.isSuccess) {
        setDashboardData(response);
        setLastUpdated(new Date());
      } else {
        setError(response?.message || "Failed to load dashboard data");
      }
    } catch (err) {
      // Ignore cancels/aborts (navigate away, refresh, interval refresh)
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;

      console.error("Error loading admin dashboard:", err);
      setError("An error occurred while loading dashboard data");
    } finally {
      inFlightRef.current = false;
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  }, [session]);

  useEffect(() => {
    if (hasPermission || isAdmin) {
      loadDashboardData({ background: false });
    }

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [hasPermission, isAdmin, loadDashboardData]);

  useEffect(() => {
    if (!hasPermission && !isAdmin) return;

    const id = setInterval(() => {
      loadDashboardData({ background: true });
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(id);
  }, [hasPermission, isAdmin, loadDashboardData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    if (!value || isNaN(value)) return "0.00%";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const calculateAverageBillValue = (totalSales, billCount) => {
    if (!billCount || billCount === 0) return 0;
    return totalSales / billCount;
  };

  if (!hasPermission && !isAdmin) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            You don't have permission to access this page.
          </Alert>
        </div>
      </div>
    );
  }

  // Calculate derived values
  const mtdChange = calculatePercentageChange(
    dashboardData?.mtdSales?.currentMonthSales || 0,
    dashboardData?.mtdSales?.lastMonthSales || 0
  );

  const todayABV = calculateAverageBillValue(
    dashboardData?.averageBillValue?.todayTotalSales || 0,
    dashboardData?.averageBillValue?.todayBillCount || 0
  );

  const yesterdayABV = calculateAverageBillValue(
    dashboardData?.averageBillValue?.yesterdayTotalSales || 0,
    dashboardData?.averageBillValue?.yesterdayBillCount || 0
  );

  const abvChange = calculatePercentageChange(todayABV, yesterdayABV);

  // Backend already combines invoice + receipt collections into PaymentCollection
  const totalCash = dashboardData?.paymentCollection?.cashAmount || 0;
  const totalCard = dashboardData?.paymentCollection?.cardAmount || 0;
  const totalBank = dashboardData?.paymentCollection?.bankAmount || 0;

  return (
    <div className="page-content gms-admin-dashboard">
      <div className="container-fluid">
        <Breadcrumb title="Admin Dashboard" breadcrumbItem="Dashboard" />

        {error ? <Alert color="danger">{error}</Alert> : null}

        {!dashboardData && initialLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
            <ClipLoader color="#556ee6" size={50} />
          </div>
        ) : null}

        {!dashboardData && !initialLoading && !error ? (
          <Alert color="info">No data available</Alert>
        ) : null}

        {dashboardData ? (
          <>
            {/* ROW 1 — OPERATIONS */}
            <Row className="mb-4">
              <Col lg={8}>
                <h4 className="mb-3">
                  <Calendar className="me-2" size={24} />
                  Operations
                </h4>
              </Col>
              <Col lg={4} className="d-flex justify-content-lg-end align-items-center gap-2">
                <small className="text-muted d-none d-lg-inline">
                  Last updated: {lastUpdated ? lastUpdated.toLocaleString() : "—"}
                </small>
                <Button
                  color="primary"
                  onClick={() => loadDashboardData({ background: true })}
                  disabled={initialLoading || isRefreshing}
                >
                  {isRefreshing ? (
                    <span className="d-inline-flex align-items-center">
                      <ClipLoader color="#ffffff" size={16} />
                      <span className="ms-2">Refreshing</span>
                    </span>
                  ) : (
                    "Refresh"
                  )}
                </Button>
              </Col>
            </Row>

        <Row className="mb-4">
          {/* Tile 1 – Today's Bookings */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.primary.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.primary.bg} 0%, ${TILE_ACCENT_STYLES.primary.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {dashboardData.todaysBookings?.totalBookings || 0}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Today's Bookings</p>
                <div style={{ fontSize: '0.85rem' }}>
                  <div className="mb-1">
                    <span className="text-info">Ongoing: {dashboardData.todaysBookings?.ongoing || 0}</span>
                  </div>
                  <div className="mb-1">
                    <span className="text-success">Completed: {dashboardData.todaysBookings?.completed || 0}</span>
                  </div>
                  <div>
                    <span className="text-danger">Pending: {dashboardData.todaysBookings?.pending || 0}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 2 – Customers in Service */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.info.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.info.bg} 0%, ${TILE_ACCENT_STYLES.info.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {dashboardData.customersInService?.customersInService || 0}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Customers in Service</p>
                <small className="text-info" style={{ fontSize: '0.85rem' }}>Currently checked-in</small>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 3 – Cancelled / Rescheduled */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.warning.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.warning.bg} 0%, ${TILE_ACCENT_STYLES.warning.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Cancelled / Rescheduled</p>
                <div style={{ fontSize: '0.85rem' }}>
                  <div className="mb-2">
                    <div className="mb-1">
                      <span className="text-danger">
                        Cancelled Today: {dashboardData.cancelledRescheduled?.cancelledToday || 0}
                      </span>
                    </div>
                    {(dashboardData.cancelledRescheduled?.cancelledRevenueLost || 0) > 0 && (
                      <div className="text-danger" style={{ fontWeight: '600' }}>
                        {formatCurrency(dashboardData.cancelledRescheduled?.cancelledRevenueLost || 0)} Lost
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-muted">
                      Rescheduled Today: {dashboardData.cancelledRescheduled?.rescheduledToday || 0}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 4 – Staff on Duty */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.danger.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.danger.bg} 0%, ${TILE_ACCENT_STYLES.danger.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {dashboardData.staffOnDuty?.onDutyCount || 0} / {dashboardData.staffOnDuty?.totalStaffCount || 0}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Staff on Duty</p>
                <small className="text-info" style={{ fontSize: '0.85rem' }}>
                  {dashboardData.staffOnDuty?.totalStaffCount > 0 
                    ? `${Math.round(((dashboardData.staffOnDuty?.onDutyCount || 0) / dashboardData.staffOnDuty.totalStaffCount) * 100)}% attendance`
                    : 'No staff data'}
                </small>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ROW 2 — FINANCE */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-3">
              <DollarSign className="me-2" size={24} />
              Finance
            </h4>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Tile 5 – MTD Sales */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.success.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.success.bg} 0%, ${TILE_ACCENT_STYLES.success.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {formatCurrency(dashboardData.mtdSales?.currentMonthSales || 0)}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>MTD Sales</p>
                <div className="d-flex align-items-center">
                  {mtdChange >= 0 ? (
                    <TrendingUp className="text-success me-1" size={16} />
                  ) : (
                    <TrendingDown className="text-danger me-1" size={16} />
                  )}
                  <span className={mtdChange >= 0 ? "text-success" : "text-danger"} style={{ fontSize: '0.85rem' }}>
                    {formatPercentage(mtdChange)} vs Last Month
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 6 – Today's Sales */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.primary.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.primary.bg} 0%, ${TILE_ACCENT_STYLES.primary.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className="mb-2 text-primary" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {formatCurrency(dashboardData.todaysSales?.todaysSales || 0)}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Today's Sales</p>
                <small className="text-primary" style={{ fontSize: '0.85rem' }}>Gross Sales</small>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 7 – Average Bill Value */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.warning.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.warning.bg} 0%, ${TILE_ACCENT_STYLES.warning.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {formatCurrency(todayABV)}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Average Bill Value</p>
                <div className="d-flex align-items-center">
                  {abvChange >= 0 ? (
                    <TrendingUp className="text-success me-1" size={16} />
                  ) : (
                    <TrendingDown className="text-danger me-1" size={16} />
                  )}
                  <span className={abvChange >= 0 ? "text-success" : "text-danger"} style={{ fontSize: '0.85rem' }}>
                    {formatPercentage(abvChange)} vs Yesterday
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 8 – Cash / Card / Bank Collection */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.purple.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.purple.bg} 0%, ${TILE_ACCENT_STYLES.purple.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Payment Collection (Today)</p>
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small">
                      <Wallet className="me-1" size={14} />
                      Cash
                    </span>
                    <strong>{formatCurrency(totalCash)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small">
                      <CreditCard className="me-1" size={14} />
                      Card
                    </span>
                    <strong>{formatCurrency(totalCard)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">
                      <Building2 className="me-1" size={14} />
                      Bank
                    </span>
                    <strong>{formatCurrency(totalBank)}</strong>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ROW 3 — CONTROL & REMINDERS */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-3">
              <AlertCircle className="me-2" size={24} />
              Control & Reminders
            </h4>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Tile 9 – Birthdays This Month */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ position: 'relative', overflow: 'hidden', border: `1px solid ${TILE_ACCENT_STYLES.secondary.bg}`, minHeight: '140px' }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${TILE_ACCENT_STYLES.secondary.bg} 0%, ${TILE_ACCENT_STYLES.secondary.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody className="text-center" style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                <h4 className="mb-2" style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {dashboardData.birthdays?.birthdaysThisMonth || 0}
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>Birthdays This Month</p>
              </CardBody>
            </Card>
          </Col>

          {/* Tile 10 – Pending / Unsettled Bills */}
          <Col lg={3} md={6} className="mb-3">
            <Card className="h-100 shadow-sm" style={{ 
              position: 'relative', 
              overflow: 'hidden',
              border: (dashboardData.unsettledBills?.unsettledBillValue || 0) > 0 
                ? `1px solid ${TILE_ACCENT_STYLES.danger.bg}` 
                : `1px solid ${TILE_ACCENT_STYLES.secondary.bg}`,
              minHeight: '140px'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '60px',
                height: '60px',
                background: (dashboardData.unsettledBills?.unsettledBillValue || 0) > 0
                  ? `linear-gradient(135deg, ${TILE_ACCENT_STYLES.danger.bg} 0%, ${TILE_ACCENT_STYLES.danger.light} 100%)`
                  : `linear-gradient(135deg, ${TILE_ACCENT_STYLES.secondary.bg} 0%, ${TILE_ACCENT_STYLES.secondary.light} 100%)`,
                borderRadius: '0 0 0 50px',
                zIndex: 1
              }}></div>
              <CardBody style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h4 className={`mb-2 ${(dashboardData.unsettledBills?.unsettledBillValue || 0) > 0 ? 'text-danger' : ''}`} 
                    style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                  {dashboardData.unsettledBills?.unsettledBillCount || 0}
                </h4>
                <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Pending / Unsettled Bills</p>
                <strong className={(dashboardData.unsettledBills?.unsettledBillValue || 0) > 0 ? 'text-danger' : 'text-muted'} 
                         style={{ fontSize: '0.9rem' }}>
                  {formatCurrency(dashboardData.unsettledBills?.unsettledBillValue || 0)}
                </strong>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* GRAPHS & CHARTS */}
        <Row className="mb-4">
          <Col lg={12}>
            <h4 className="mb-3">
              <BarChart3 className="me-2" size={24} />
              Analytics
            </h4>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Bar Graph – Sales Trend (12 months) */}
          <Col lg={8} className="mb-3">
            <Card className="shadow-sm">
              <CardBody>
                <CardTitle tag="h5" className="mb-4">Sales Trend - 12 Months</CardTitle>
                {dashboardData.salesTrend && dashboardData.salesTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={dashboardData.salesTrend}
                      margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="monthLabel" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fill: '#666', fontSize: 12 }}
                        stroke="#999"
                      />
                      <YAxis 
                        tick={{ fill: '#666', fontSize: 12 }}
                        stroke="#999"
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div style={{
                                backgroundColor: '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '10px 12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                              }}>
                                <p style={{ margin: 0, fontWeight: 'bold', color: '#000', marginBottom: '4px' }}>
                                  {data.monthLabel}
                                </p>
                                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                  Sales: {formatCurrency(data.monthlySales)}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar 
                        dataKey="monthlySales" 
                        radius={[8, 8, 0, 0]}
                      >
                        {dashboardData.salesTrend.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert color="info" className="mb-0">
                    No sales data available for the last 12 months.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Pie Chart – Source Distribution (This Month) */}
          <Col lg={4} className="mb-3">
            <Card className="shadow-sm">
              <CardBody>
                <CardTitle tag="h5" className="mb-4">Source Distribution (This Month)</CardTitle>
                {dashboardData.sourceDistribution && dashboardData.sourceDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={dashboardData.sourceDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="bookingCount"
                        nameKey="sourceName"
                      >
                        {dashboardData.sourceDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            const sourceName = data.sourceName || 'Unknown';
                            const bookingCount = data.bookingCount || 0;
                            
                            // Calculate percentage from total bookings
                            const totalBookings = dashboardData.sourceDistribution.reduce(
                              (sum, item) => sum + (item.bookingCount || 0), 
                              0
                            );
                            const percent = totalBookings > 0 
                              ? ((bookingCount / totalBookings) * 100).toFixed(1)
                              : '0.0';
                            
                            return (
                              <div style={{
                                backgroundColor: '#fff',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px 12px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <p style={{ margin: 0, fontWeight: 'bold', color: '#000' }}>
                                  {sourceName}
                                </p>
                                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                                  {bookingCount} booking{bookingCount !== 1 ? 's' : ''} ({percent}%)
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert color="info" className="mb-0">
                    No source data available for this month.
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AdminDashboard;

